/* eslint-disable no-restricted-imports */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  VStack,
  HStack,
  Text,
  Spinner,
  Box,
  Input,
  Tag,
  TagLabel,
  TagCloseButton,
  Flex,
} from '@chakra-ui/react';
import { Button } from 'src/components/atoms/Button';
import { Checkbox } from 'src/components/atoms/Checkbox';
import {
  AdAccount,
  IntegrationService,
  useAdAccountsQuery,
} from 'src/api/queries';
import { SINGLE_ACCOUNT_SERVICES } from 'src/features/Settings/utils/constants';
import {
  initialSelection,
  nextSelection,
} from 'src/features/Settings/utils/accountSelection';

// DV360 only: users without partner-level access get an empty accounts list from the API
// (advertisers cannot be listed without a partner), so they enter advertiser IDs manually.
const isValidDv360AdvertiserId = (value: string) => /^\d+$/.test(value.trim());

type Dv360AdvertiserIdEntryProps = {
  advertiserIds: string[];
  onAdd: (id: string) => void;
  onRemove: (id: string) => void;
};

const Dv360AdvertiserIdEntry: React.FC<Dv360AdvertiserIdEntryProps> = ({
  advertiserIds,
  onAdd,
  onRemove,
}) => {
  const [inputValue, setInputValue] = useState('');

  const canAdd =
    isValidDv360AdvertiserId(inputValue) &&
    !advertiserIds.includes(inputValue.trim());

  const handleAdd = useCallback(() => {
    if (!canAdd) return;
    onAdd(inputValue.trim());
    setInputValue('');
  }, [canAdd, inputValue, onAdd]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        handleAdd();
      }
    },
    [handleAdd],
  );

  return (
    <VStack align="stretch" spacing={4}>
      <Text fontSize="sm" color="content.muted" fontFamily="marketingPage">
        No accessible ad accounts were found for this login. Enter your DV360
        advertiser ID(s) manually.
      </Text>
      <HStack>
        <Input
          placeholder="Advertiser ID"
          size="md"
          fontFamily="marketingPage"
          value={inputValue}
          onChange={(event) => setInputValue(event.target.value)}
          onKeyDown={handleKeyDown}
        />
        <Button
          bg="surface.sidebarMuted"
          color="content.body"
          size="md"
          px={6}
          borderRadius="md"
          fontWeight="600"
          buttonName="add-dv360-advertiser-id"
          fontFamily="marketingPage"
          onClick={handleAdd}
          isDisabled={!canAdd}
          _hover={{ bg: 'sidebar.itemHoverBg' }}>
          Add
        </Button>
      </HStack>
      {advertiserIds.length > 0 && (
        <Flex wrap="wrap" gap={2}>
          {advertiserIds.map((id) => (
            <Tag key={id} size="md" borderRadius="md" variant="subtle">
              <TagLabel fontFamily="marketingPage">{id}</TagLabel>
              <TagCloseButton onClick={() => onRemove(id)} />
            </Tag>
          ))}
        </Flex>
      )}
    </VStack>
  );
};

type SelectAccountsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSave: (selectedAccountIds: string[]) => void;
  service: IntegrationService;
  customer: string;
  isSaving: boolean;
  onError?: (title: string, description: string) => void;
};

export const SelectAccountsModal: React.FC<SelectAccountsModalProps> = ({
  isOpen,
  onClose,
  onSave,
  service,
  customer,
  isSaving,
  onError,
}) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [dv360AdvertiserIds, setDv360AdvertiserIds] = useState<string[]>([]);

  const { data, isLoading, isError, error, remove } = useAdAccountsQuery(
    { customer, service },
    isOpen && Boolean(customer),
  );

  useEffect(() => {
    if (!isError || !error) return;

    const status = (error as { response?: { status?: number } })?.response
      ?.status;
    const isAuthError = status === 401;

    onError?.(
      'Failed to load ad accounts',
      isAuthError
        ? 'Authorization expired. Please reconnect this service.'
        : 'Please try again or contact support.',
    );
    remove();
    onClose();
  }, [isError, error, onError, onClose, remove]);

  const accounts = useMemo(() => data?.accounts ?? [], [data]);

  const isSingleSelect = SINGLE_ACCOUNT_SERVICES.includes(service);

  const isDv360AdvertiserEntryMode =
    !isLoading && accounts.length === 0 && service === 'dv360';

  useEffect(() => {
    if (!data) return;

    if (data.selectedAccountIds) {
      setSelectedIds(new Set(data.selectedAccountIds));
      setDv360AdvertiserIds(data.selectedAccountIds);
    } else {
      setSelectedIds(
        initialSelection(
          accounts.map((a) => a.id),
          isSingleSelect,
        ),
      );
      setDv360AdvertiserIds([]);
    }
  }, [data, accounts, isSingleSelect]);

  const handleToggle = useCallback(
    (accountId: string) => {
      setSelectedIds((prev) => nextSelection(prev, accountId, isSingleSelect));
    },
    [isSingleSelect],
  );

  const allSelected =
    accounts.length > 0 && selectedIds.size === accounts.length;

  const handleToggleAll = useCallback(() => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(accounts.map((a) => a.id)));
    }
  }, [allSelected, accounts]);

  const handleAddDv360AdvertiserId = useCallback((id: string) => {
    setDv360AdvertiserIds((prev) => [...prev, id]);
  }, []);

  const handleRemoveDv360AdvertiserId = useCallback((id: string) => {
    setDv360AdvertiserIds((prev) =>
      prev.filter((existingId) => existingId !== id),
    );
  }, []);

  const handleSave = useCallback(() => {
    onSave(
      isDv360AdvertiserEntryMode ? dv360AdvertiserIds : Array.from(selectedIds),
    );
  }, [onSave, isDv360AdvertiserEntryMode, dv360AdvertiserIds, selectedIds]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered size="md">
      <ModalOverlay />
      <ModalContent borderRadius="16px" bg="surface.panel">
        <ModalCloseButton />
        <ModalHeader pb={2} pt={6}>
          <Text
            fontFamily="marketingPage"
            fontWeight="bold"
            fontSize="xl"
            color="content.title">
            Select Ad Accounts
          </Text>
        </ModalHeader>

        <ModalBody px={6} py={4}>
          {isLoading ? (
            <VStack py={8}>
              <Spinner size="lg" color="icon.green" />
              <Text
                fontSize="sm"
                color="content.muted"
                fontFamily="marketingPage">
                Loading accounts...
              </Text>
            </VStack>
          ) : isDv360AdvertiserEntryMode ? (
            <Dv360AdvertiserIdEntry
              advertiserIds={dv360AdvertiserIds}
              onAdd={handleAddDv360AdvertiserId}
              onRemove={handleRemoveDv360AdvertiserId}
            />
          ) : accounts.length === 0 ? (
            <Text
              fontSize="sm"
              color="content.muted"
              fontFamily="marketingPage">
              No ad accounts found for this connection.
            </Text>
          ) : (
            <VStack align="stretch" spacing={0}>
              {isSingleSelect ? (
                <Box p={2} borderBottomWidth="1px" borderColor="surface.border">
                  <Text
                    fontSize="sm"
                    fontFamily="marketingPage"
                    fontWeight="semibold">
                    Select one account
                  </Text>
                </Box>
              ) : (
                <HStack
                  p={2}
                  borderBottomWidth="1px"
                  borderColor="surface.border"
                  cursor="pointer"
                  onClick={handleToggleAll}>
                  <Checkbox
                    isChecked={allSelected}
                    isIndeterminate={
                      selectedIds.size > 0 && selectedIds.size < accounts.length
                    }
                    pointerEvents="none"
                  />
                  <Text
                    fontSize="sm"
                    fontFamily="marketingPage"
                    fontWeight="semibold">
                    Select all ({accounts.length})
                  </Text>
                </HStack>
              )}

              <Box maxH="300px" overflowY="auto">
                {accounts.map((account: AdAccount) => (
                  <HStack
                    key={account.id}
                    p={2}
                    borderRadius="md"
                    _hover={{ bg: 'sidebar.itemHoverBg' }}
                    cursor="pointer"
                    onClick={() => handleToggle(account.id)}>
                    <Checkbox
                      isChecked={selectedIds.has(account.id)}
                      pointerEvents="none"
                    />
                    <VStack align="start" spacing={0}>
                      <Text fontSize="sm" fontFamily="marketingPage">
                        {account.name}
                      </Text>
                      <Text fontSize="xs" color="content.muted">
                        {account.id}
                      </Text>
                    </VStack>
                  </HStack>
                ))}
              </Box>
            </VStack>
          )}
        </ModalBody>

        <ModalFooter gap={3} px={6} pb={6}>
          <Button
            bg="surface.sidebarMuted"
            color="content.body"
            size="md"
            px={6}
            py={2}
            borderRadius="md"
            fontWeight="600"
            buttonName="cancel-select-accounts"
            fontFamily="marketingPage"
            onClick={handleClose}
            flex={1}
            _hover={{ bg: 'sidebar.itemHoverBg' }}>
            Cancel
          </Button>
          <Button
            bg="icon.green"
            color="white"
            size="md"
            px={6}
            py={2}
            borderRadius="md"
            fontWeight="600"
            buttonName="save-select-accounts"
            fontFamily="marketingPage"
            onClick={handleSave}
            isLoading={isSaving}
            isDisabled={
              (isDv360AdvertiserEntryMode
                ? dv360AdvertiserIds.length === 0
                : isSingleSelect
                  ? selectedIds.size !== 1
                  : selectedIds.size === 0) || isLoading
            }
            flex={1}
            _hover={{ bg: 'green.600' }}>
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
